// src/components/CommentBankManager.jsx
// Comment Bank Management - Head marker manages reusable feedback comments

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  getAllCommentBanks,
  addComment,
  updateComment,
  deleteComment,
  approveSuggestion,
  rejectSuggestion,
  addFinalQuestionComment,
  deleteFinalQuestionComment,
  updateFinalQuestionComment
} from '../firebase/comments';
import { addMyComment, addMyFinalQuestionComment } from '../firebase/markerComments';
import { getHomeworkNames, getSectionStructures, getQuestionStructures, sortHomeworkTypes } from '../firebase/settings';
import { getAllMarkers } from '../firebase/users';

// â”€â”€â”€ helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Normalises a section value from the bank regardless of whether it's still a
// plain array (old data) or the new { headers, comments } object.
function normaliseSectionData(raw) {
  if (!raw) return { headers: [], comments: [] };
  if (Array.isArray(raw)) return { headers: [], comments: raw };
  return {
    headers: raw.headers || [],
    comments: raw.comments || []
  };
}
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function CommentBankManager({ user }) {
  const [commentBanks, setCommentBanks] = useState({});
  const [homeworkNames, setHomeworkNames] = useState({});
  const [selectedHomework, setSelectedHomework] = useState('');
  const [selectedSection, setSelectedSection] = useState('A');
  const [loading, setLoading] = useState(true);

  // single-add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentHeader, setNewCommentHeader] = useState('');

  // bulk-add form
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkCommentText, setBulkCommentText] = useState('');
  const [bulkCommentHeader, setBulkCommentHeader] = useState('');

  // header management
  const [newHeaderText, setNewHeaderText] = useState('');
  const [showHeaderManager, setShowHeaderManager] = useState(false);

  // edit / delete
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [editHeader, setEditHeader] = useState('');

  const [pendingSuggestionsCount, setPendingSuggestionsCount] = useState(0);
  const [showPendingList, setShowPendingList] = useState(false);
  const [questionStructures, setQuestionStructures] = useState({});
  const [editingFQComment, setEditingFQComment] = useState(null);
  const [editFQText, setEditFQText] = useState('');
  const [newFQCommentText, setNewFQCommentText] = useState({});

  // allocate-to-marker
  const [markers, setMarkers] = useState([]);
  const [allocateTarget, setAllocateTarget] = useState({}); // suggestionId -> markerId
  const [fqAllocateOpen, setFqAllocateOpen] = useState(null); // commentId of the open allocate row, or null
  const [fqAllocateMarkerId, setFqAllocateMarkerId] = useState('');

  // drag state
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const homeworkTypes = sortHomeworkTypes(Object.keys(homeworkNames));
  const [sectionStructures, setSectionStructures] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [banks, names, structures, qStructures, markerList] = await Promise.all([getAllCommentBanks(), getHomeworkNames(), getSectionStructures(), getQuestionStructures(), getAllMarkers()]);
      setCommentBanks(banks);
      setHomeworkNames(names);
      setSectionStructures(structures);
      setQuestionStructures(qStructures);
      setMarkers(markerList);
      const sorted = sortHomeworkTypes(Object.keys(names));
      // Stay on the homework being worked on (loadData also runs after every
      // approve/reject/allocate) - only default to the first on first load
      setSelectedHomework(current => (current && names[current] !== undefined ? current : (sorted[0] || '')));
      let count = 0;
      Object.values(banks).forEach(bank => { count += (bank.suggestions || []).length; });
      setPendingSuggestionsCount(count);
    } catch (error) {
      console.error('Failed to load comment banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionKey = () => selectedSection === 'final' ? 'final' : `section${selectedSection}`;

  const getCurrentSectionData = () => {
    const bank = commentBanks[selectedHomework] || {};
    return normaliseSectionData(bank[getSectionKey()]);
  };

  const getCurrentComments = () => getCurrentSectionData().comments;
  const getCurrentHeaders  = () => getCurrentSectionData().headers;

  const getPendingSuggestions = () => {
    const bank = commentBanks[selectedHomework] || {};
    return (bank.suggestions || []).filter(s =>
      s.section !== 'finalQuestion' &&
      (s.section === selectedSection || (selectedSection === 'final' && s.section === 'final'))
    );
  };

  // Every pending suggestion, grouped by homework and the part it's for -
  // behind the orange "N pending suggestions" button, each row a shortcut
  // to that homework/section
  const getPendingBreakdown = () => {
    const rows = [];
    homeworkTypes.forEach(hw => {
      const groups = new Map();
      ((commentBanks[hw] || {}).suggestions || []).forEach(sg => {
        const isFQ = sg.section === 'finalQuestion';
        const key = isFQ ? `fq:${sg.questionId}` : `s:${sg.section}`;
        if (!groups.has(key)) {
          const sectionName = (sectionStructures[hw]?.sections || []).find(sec => sec.id === sg.section)?.name;
          const label = isFQ
            ? `${sg.questionLabel || 'Question'} (final question comment)`
            : sg.section === 'final' ? 'Final comments' : (sectionName || `Section ${sg.section}`);
          groups.set(key, { label, count: 0, section: isFQ ? 'finalQuestions' : sg.section });
        }
        groups.get(key).count += 1;
      });
      if (groups.size) rows.push({ homework: hw, name: homeworkNames[hw] || hw, parts: [...groups.values()] });
    });
    return rows;
  };

  const jumpToPending = (homework, section) => {
    setSelectedHomework(homework);
    setSelectedSection(section);
    setShowPendingList(false);
  };

  const getFQPendingSuggestions = (questionId) => {
    const bank = commentBanks[selectedHomework] || {};
    return (bank.suggestions || []).filter(s => s.section === 'finalQuestion' && s.questionId === questionId);
  };

  // â”€â”€ header management â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleAddHeader = async () => {
    if (!newHeaderText.trim()) return;
    const sectionData = getCurrentSectionData();
    if (sectionData.headers.includes(newHeaderText.trim())) {
      alert('That header already exists');
      return;
    }
    const updated = { ...sectionData, headers: [...sectionData.headers, newHeaderText.trim()] };
    await saveSectionData(updated);
    setNewHeaderText('');
  };

  const handleDeleteHeader = async (header) => {
    if (!window.confirm(`Delete header "${header}"?\n\nComments under this header will become uncategorised.`)) return;
    const sectionData = getCurrentSectionData();
    const updated = {
      headers: sectionData.headers.filter(h => h !== header),
      comments: sectionData.comments.map(c => c.header === header ? { ...c, header: null } : c)
    };
    await saveSectionData(updated);
  };

  const handleReorderHeaders = async (fromIdx, toIdx) => {
    const sectionData = getCurrentSectionData();
    const headers = [...sectionData.headers];
    const [moved] = headers.splice(fromIdx, 1);
    headers.splice(toIdx, 0, moved);
    await saveSectionData({ ...sectionData, headers });
  };

  // â”€â”€ comments â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) { alert('Please enter comment text'); return; }
    try {
      await addComment(selectedHomework, selectedSection, newCommentText.trim(), user.uid, user.name, newCommentHeader || null);
      setNewCommentText('');
      setNewCommentHeader('');
      setShowAddForm(false);
      await loadData();
    } catch (error) {
      console.error('Add comment error:', error);
      alert('Failed to add comment');
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkCommentText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return;
    try {
      for (const line of lines) {
        await addComment(selectedHomework, selectedSection, line, user.uid, user.name, bulkCommentHeader || null);
      }
      setBulkCommentText('');
      setBulkCommentHeader('');
      setShowBulkAdd(false);
      await loadData();
      alert(`${lines.length} comment${lines.length !== 1 ? 's' : ''} added successfully!`);
    } catch (error) {
      alert('Failed to add some comments. Please try again.');
    }
  };

  const handleStartEdit = (comment) => {
    setEditingComment(comment.id);
    setEditText(comment.text);
    setEditHeader(comment.header || '');
  };

  const handleSaveEdit = async (comment) => {
    if (!editText.trim()) { alert('Comment cannot be empty'); return; }
    try {
      await updateComment(selectedHomework, selectedSection, comment.id, editText, editHeader || null);
      setEditingComment(null);
      setEditText('');
      setEditHeader('');
      await loadData();
    } catch (error) {
      console.error('Update comment error:', error);
      alert('Failed to update comment');
    }
  };

  const handleCancelEdit = () => { setEditingComment(null); setEditText(''); setEditHeader(''); };

  const handleDeleteComment = async (comment) => {
    if (!window.confirm(`Delete this comment?\n\n"${comment.text}"\n\nThis cannot be undone.`)) return;
    try {
      await deleteComment(selectedHomework, selectedSection, comment.id);
      await loadData();
    } catch (error) {
      console.error('Delete comment error:', error);
      alert('Failed to delete comment');
    }
  };

  // â”€â”€ drag to reassign header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleDragStart = (commentId) => { dragItem.current = commentId; };

  const handleDragEnterHeader = (header) => { dragOver.current = header; };

  const handleDrop = async (targetHeader) => {
    if (dragItem.current == null) return;
    const sectionData = getCurrentSectionData();
    const updated = {
      ...sectionData,
      comments: sectionData.comments.map(c =>
        c.id === dragItem.current ? { ...c, header: targetHeader } : c
      )
    };
    await saveSectionData(updated);
    dragItem.current = null;
    dragOver.current = null;
  };

  // â”€â”€ firebase write â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Writes the full { headers, comments } object for the current section.
  // The firebase/comments.js addComment / updateComment / deleteComment functions
  // will need updating to handle the new structure — see note at bottom of file.
  // For now this directly calls the Firestore update via a new exported function.
  const saveSectionData = async (sectionData) => {
    try {
      const { updateSectionData } = await import('../firebase/comments');
      await updateSectionData(selectedHomework, getSectionKey(), sectionData);
      await loadData();
    } catch (error) {
      console.error('Failed to save section data:', error);
      alert('Failed to save. Please try again.');
    }
  };

  // â”€â”€ approve / reject suggestions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleApproveSuggestion = async (suggestion) => {
    try {
      await approveSuggestion(selectedHomework, suggestion.id);
      await loadData();
      alert(`Suggestion approved and added to ${selectedHomework} ${suggestion.section}!`);
    } catch (error) {
      console.error('Approve suggestion error:', error);
      alert('Failed to approve suggestion');
    }
  };

  const handleRejectSuggestion = async (suggestion) => {
    if (!window.confirm(`Reject this suggestion?\n\n"${suggestion.text}"`)) return;
    try {
      await rejectSuggestion(selectedHomework, suggestion.id);
      await loadData();
    } catch (error) {
      console.error('Reject suggestion error:', error);
      alert('Failed to reject suggestion');
    }
  };

  // â”€â”€ allocate to marker's personal bank â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Sends a copy of the comment directly into one marker's own bank - no
  // approval step, not visible to any other marker.

  const handleAllocateSuggestion = async (suggestion) => {
    const targetMarkerId = allocateTarget[suggestion.id] || suggestion.suggestedBy;
    if (!targetMarkerId) { alert('Select a marker first'); return; }
    try {
      if (suggestion.section === 'finalQuestion') {
        await addMyFinalQuestionComment(targetMarkerId, selectedHomework, suggestion.questionId, suggestion.text);
      } else {
        await addMyComment(targetMarkerId, selectedHomework, suggestion.section, suggestion.text, null);
      }
      // Resolved via allocation rather than approval - remove from the pending queue
      await rejectSuggestion(selectedHomework, suggestion.id);
      await loadData();
    } catch (error) {
      console.error('Allocate suggestion error:', error);
      alert('Failed to allocate comment to marker');
    }
  };

  const handleAllocateComment = async (comment, targetMarkerId) => {
    if (!targetMarkerId) { alert('Select a marker first'); return; }
    try {
      await addMyComment(targetMarkerId, selectedHomework, selectedSection, comment.text, comment.header || null);
      const markerName = markers.find(m => m.id === targetMarkerId)?.name || 'marker';
      alert(`Added to ${markerName}'s personal comment bank`);
    } catch (error) {
      console.error('Allocate comment error:', error);
      alert('Failed to allocate comment to marker');
    }
  };

  const handleAllocateFinalQuestionComment = async (comment, questionId, targetMarkerId) => {
    if (!targetMarkerId) { alert('Select a marker first'); return; }
    try {
      await addMyFinalQuestionComment(targetMarkerId, selectedHomework, questionId, comment.text);
      const markerName = markers.find(m => m.id === targetMarkerId)?.name || 'marker';
      alert(`Added to ${markerName}'s personal comment bank`);
    } catch (error) {
      console.error('Allocate final question comment error:', error);
      alert('Failed to allocate comment to marker');
    }
  };

  // â”€â”€ render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading comment banks...</div>;
  }

  const currentComments    = getCurrentComments();
  const currentHeaders     = getCurrentHeaders();
  const pendingSuggestions = getPendingSuggestions();

  // Group comments: one bucket per header, plus uncategorised
  const grouped = {};
  currentHeaders.forEach(h => { grouped[h] = []; });
  grouped['__none__'] = [];
  currentComments.forEach(c => {
    const key = (c.header && currentHeaders.includes(c.header)) ? c.header : '__none__';
    grouped[key].push(c);
  });

  const headerDropStyle = (isOver) => ({
    padding: '6px 12px',
    background: isOver ? '#e8f4fd' : '#f8f9fa',
    border: `2px dashed ${isOver ? '#667eea' : '#e2e8f0'}`,
    borderRadius: '6px',
    fontSize: '12px',
    color: '#7f8c8d',
    textAlign: 'center',
    transition: 'all 0.15s'
  });

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0' }}>Comment Bank Manager</h2>
        <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
          Manage reusable feedback comments for markers
          {pendingSuggestionsCount > 0 && (
            <button
              type="button"
              onClick={() => setShowPendingList(v => !v)}
              title="Show which questions these are for"
              style={{ marginLeft: '12px', padding: '4px 10px 4px 12px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              {pendingSuggestionsCount} pending suggestion{pendingSuggestionsCount !== 1 ? 's' : ''}
              {showPendingList ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
        {showPendingList && pendingSuggestionsCount > 0 && (
          <div style={{ marginTop: '14px', padding: '12px 16px', background: '#fffaf0', border: '1px solid #f6d7a7', borderRadius: '8px' }}>
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '8px' }}>Click a question to go to its suggestions.</div>
            {getPendingBreakdown().map(group => (
              <div key={group.homework} style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#2c3e50', marginBottom: '4px' }}>{group.name}</div>
                {group.parts.map(part => (
                  <button
                    key={part.label}
                    type="button"
                    onClick={() => jumpToPending(group.homework, part.section)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '520px', padding: '6px 10px', marginBottom: '4px', background: 'white', border: '1px solid #f6d7a7', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#2c3e50', textAlign: 'left' }}
                  >
                    <span>{part.label}</span>
                    <span style={{ background: '#f39c12', color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '12px', fontWeight: '700' }}>{part.count}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selectors */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>Homework</label>
            <select value={selectedHomework} onChange={(e) => setSelectedHomework(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}>
              {homeworkTypes.map(type => (
                <option key={type} value={type}>{homeworkNames[type] || type}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>Section</label>
            <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}>
              {(sectionStructures[selectedHomework]?.sections || [
                { id: 'A', name: 'Section A' },
                { id: 'B', name: 'Section B' },
                { id: 'C', name: 'Section C' }
              ]).map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
              <option value="final">Final Comments</option>
              <option value="finalQuestions">Final Question Comments</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pending Suggestions */}
      {pendingSuggestions.length > 0 && selectedSection !== 'finalQuestions' && (
        <div style={{ background: '#fffaf0', border: '2px solid #f39c12', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#f39c12' }}>Pending Suggestions ({pendingSuggestions.length})</h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {pendingSuggestions.map(suggestion => (
              <div key={suggestion.id} style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #f39c12' }}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '14px', marginBottom: '8px' }}>{suggestion.text}</div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                    Suggested by: {suggestion.suggestedByName} • {new Date(suggestion.suggestedAt).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => handleApproveSuggestion(suggestion)}
                    style={{ padding: '8px 16px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    Approve
                  </button>
                  <button onClick={() => handleRejectSuggestion(suggestion)}
                    style={{ padding: '8px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    Reject
                  </button>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
                    <select
                      value={allocateTarget[suggestion.id] ?? suggestion.suggestedBy ?? ''}
                      onChange={e => setAllocateTarget(prev => ({ ...prev, [suggestion.id]: e.target.value }))}
                      style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                    >
                      {markers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}{m.role === 'head_marker' ? ' (Head Marker)' : ''}</option>
                      ))}
                    </select>
                    <button onClick={() => handleAllocateSuggestion(suggestion)}
                      style={{ padding: '8px 14px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}
                      title="Add this comment straight to the selected marker's own personal comment bank - not visible to any other marker">
                      → Personal Bank
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final Question Comments Panel */}
      {selectedSection === 'finalQuestions' && (() => {
        const questions = questionStructures[selectedHomework] || [];
        const bank = commentBanks[selectedHomework] || {};
        const fqComments = bank.finalQuestions || {};

        if (questions.length === 0) {
          return (
            <div style={{ background: 'white', borderRadius: '12px', padding: '40px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', textAlign: 'center', color: '#7f8c8d' }}>
              <div style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>No questions configured</div>
              <div style={{ fontSize: '14px' }}>Add questions in Settings &gt; Homework for {selectedHomework}</div>
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {questions.map((question, qIndex) => {
              const qComments = fqComments[question.id] || [];
              const isAddingNew = (newFQCommentText[question.id] !== undefined);
              const fqSuggestions = getFQPendingSuggestions(question.id);
              return (
                <div key={question.id} style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ background: '#667eea', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
                        {qIndex + 1}
                      </span>
                      {question.label || `Question ${qIndex + 1}`}
                      <span style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: '400' }}>
                        ({qComments.length} comment{qComments.length !== 1 ? 's' : ''})
                      </span>
                      {fqSuggestions.length > 0 && (
                        <span style={{ padding: '2px 10px', background: '#f39c12', color: 'white', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                          {fqSuggestions.length} pending
                        </span>
                      )}
                    </h3>
                    {!isAddingNew && (
                      <button
                        onClick={() => setNewFQCommentText(prev => ({ ...prev, [question.id]: '' }))}
                        style={{ padding: '8px 16px', background: '#c33329', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                      >
                        + Add Comment
                      </button>
                    )}
                  </div>

                  {isAddingNew && (
                    <div style={{ marginBottom: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '2px solid #667eea' }}>
                      <textarea
                        value={newFQCommentText[question.id]}
                        onChange={(e) => setNewFQCommentText(prev => ({ ...prev, [question.id]: e.target.value }))}
                        placeholder="Enter comment text..."
                        rows={3}
                        style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '10px' }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={async () => {
                            const text = (newFQCommentText[question.id] || '').trim();
                            if (!text) { alert('Please enter comment text'); return; }
                            try {
                              await addFinalQuestionComment(selectedHomework, question.id, text, user.uid, user.name);
                              setNewFQCommentText(prev => { const next = { ...prev }; delete next[question.id]; return next; });
                              await loadData();
                            } catch (e) { alert('Failed to add comment'); }
                          }}
                          style={{ padding: '8px 16px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setNewFQCommentText(prev => { const next = { ...prev }; delete next[question.id]; return next; })}
                          style={{ padding: '8px 16px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {fqSuggestions.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '16px', background: '#fffaf0', border: '2px solid #f39c12', borderRadius: '8px' }}>
                      <div style={{ fontWeight: '600', color: '#f39c12', marginBottom: '12px', fontSize: '14px' }}>
                        Pending Suggestions ({fqSuggestions.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {fqSuggestions.map(suggestion => (
                          <div key={suggestion.id} style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #f39c12' }}>
                            <div style={{ fontSize: '14px', marginBottom: '8px' }}>{suggestion.text}</div>
                            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
                              Suggested by: {suggestion.suggestedByName} · {new Date(suggestion.suggestedAt).toLocaleDateString()}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleApproveSuggestion(suggestion)}
                                style={{ padding: '6px 14px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectSuggestion(suggestion)}
                                style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                              >
                                Reject
                              </button>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
                                <select
                                  value={allocateTarget[suggestion.id] ?? suggestion.suggestedBy ?? ''}
                                  onChange={e => setAllocateTarget(prev => ({ ...prev, [suggestion.id]: e.target.value }))}
                                  style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                                >
                                  {markers.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}{m.role === 'head_marker' ? ' (Head Marker)' : ''}</option>
                                  ))}
                                </select>
                                <button onClick={() => handleAllocateSuggestion(suggestion)}
                                  style={{ padding: '6px 12px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}
                                  title="Add this comment straight to the selected marker's own personal comment bank - not visible to any other marker">
                                  → Personal Bank
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {qComments.length === 0 && !isAddingNew && (
                      <div style={{ fontSize: '13px', color: '#7f8c8d', textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '6px' }}>
                        No comments yet
                      </div>
                    )}
                    {qComments.map(comment => (
                      <div key={comment.id} style={{ padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', background: '#f8f9fa' }}>
                        {editingFQComment?.commentId === comment.id ? (
                          <div>
                            <textarea
                              value={editFQText}
                              onChange={(e) => setEditFQText(e.target.value)}
                              rows={3}
                              style={{ width: '100%', padding: '8px', border: '2px solid #667eea', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '8px' }}
                              autoFocus
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={async () => {
                                  if (!editFQText.trim()) { alert('Comment cannot be empty'); return; }
                                  try {
                                    await updateFinalQuestionComment(selectedHomework, question.id, comment.id, editFQText.trim());
                                    setEditingFQComment(null);
                                    setEditFQText('');
                                    await loadData();
                                  } catch (e) { alert('Failed to update comment'); }
                                }}
                                style={{ padding: '6px 14px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                              >
                                Save
                              </button>
                              <button
                                onClick={() => { setEditingFQComment(null); setEditFQText(''); }}
                                style={{ padding: '6px 14px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{ fontSize: '14px', color: '#2c3e50', lineHeight: '1.5', flex: 1 }}>
                                {comment.text}
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <button
                                  onClick={() => { setEditingFQComment({ questionId: question.id, commentId: comment.id }); setEditFQText(comment.text); }}
                                  style={{ padding: '4px 10px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete this comment?\n\n"${comment.text}"`)) return;
                                    try {
                                      await deleteFinalQuestionComment(selectedHomework, question.id, comment.id);
                                      await loadData();
                                    } catch (e) { alert('Failed to delete comment'); }
                                  }}
                                  style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                                >
                                  Delete
                                </button>
                                <button
                                  onClick={() => { setFqAllocateOpen(prev => prev === comment.id ? null : comment.id); setFqAllocateMarkerId(''); }}
                                  style={{ padding: '4px 10px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                                  title="Copy this comment into a specific marker's own personal comment bank"
                                >
                                  Allocate
                                </button>
                              </div>
                            </div>
                            {fqAllocateOpen === comment.id && (
                              <div style={{ marginTop: '10px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <select value={fqAllocateMarkerId} onChange={e => setFqAllocateMarkerId(e.target.value)}
                                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                                  <option value=''>Select a marker...</option>
                                  {markers.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}{m.role === 'head_marker' ? ' (Head Marker)' : ''}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={async () => {
                                    if (!fqAllocateMarkerId) { alert('Select a marker first'); return; }
                                    await handleAllocateFinalQuestionComment(comment, question.id, fqAllocateMarkerId);
                                    setFqAllocateOpen(null);
                                    setFqAllocateMarkerId('');
                                  }}
                                  style={{ padding: '6px 14px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
                                  Send
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Comments Panel */}
      {selectedSection !== 'finalQuestions' && <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0 }}>Comments ({currentComments.length})</h3>
          {!showAddForm && !showBulkAdd && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => setShowHeaderManager(!showHeaderManager)}
                style={{ padding: '10px 16px', background: showHeaderManager ? '#e2e8f0' : '#f39c12', color: showHeaderManager ? '#4a5568' : 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                {showHeaderManager ? 'Hide Headers' : 'Manage Headers'}
              </button>
              <button onClick={() => setShowAddForm(true)}
                style={{ padding: '10px 20px', background: '#c33329', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                + Add Comment
              </button>
              <button onClick={() => setShowBulkAdd(true)}
                style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Bulk Add
              </button>
            </div>
          )}
        </div>

        {/* Header Manager */}
        {showHeaderManager && (
          <div style={{ marginBottom: '20px', padding: '16px', background: '#fffbf0', borderRadius: '8px', border: '2px solid #f39c12' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#f39c12', marginBottom: '12px' }}>
              Headers for {selectedSection === 'final' ? 'Final Comments' : `Section ${selectedSection}`} — {homeworkNames[selectedHomework] || selectedHomework}
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '12px' }}>
              Drag headers to reorder them. Comments can be dragged into header groups below.
            </div>
            {currentHeaders.length === 0 && (
              <div style={{ fontSize: '13px', color: '#95a5a6', fontStyle: 'italic', marginBottom: '12px' }}>
                No headers yet — comments will be shown as a flat list.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
              {currentHeaders.map((header, idx) => (
                <div key={header} draggable
                  onDragStart={() => { dragItem.current = `header-${idx}`; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (!dragItem.current?.startsWith('header-')) return;
                    const fromIdx = parseInt(dragItem.current.split('-')[1]);
                    handleReorderHeaders(fromIdx, idx);
                    dragItem.current = null;
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'white', borderRadius: '6px', border: '1px solid #f39c12', cursor: 'grab' }}>
                  <span style={{ color: '#bdc3c7', fontSize: '16px', lineHeight: 1 }}>⣿</span>
                  <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{header}</span>
                  <span style={{ fontSize: '12px', color: '#7f8c8d' }}>{(grouped[header] || []).length} comment{(grouped[header] || []).length !== 1 ? 's' : ''}</span>
                  <button onClick={() => handleDeleteHeader(header)}
                    style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type="text" placeholder="New header name..." value={newHeaderText}
                onChange={e => setNewHeaderText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newHeaderText.trim()) handleAddHeader(); }}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              <button onClick={handleAddHeader} disabled={!newHeaderText.trim()}
                style={{ padding: '8px 16px', background: newHeaderText.trim() ? '#f39c12' : '#e2e8f0', color: newHeaderText.trim() ? 'white' : '#95a5a6', border: 'none', borderRadius: '6px', cursor: newHeaderText.trim() ? 'pointer' : 'not-allowed', fontWeight: '700' }}>
                + Add
              </button>
            </div>
          </div>
        )}

        {/* Bulk Add Form */}
        {showBulkAdd && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '2px solid #667eea' }}>
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '10px' }}>
              Paste your comments below — one per line. All will be added to <strong>{selectedSection === 'final' ? 'Final Comments' : `Section ${selectedSection}`}</strong> for <strong>{homeworkNames[selectedHomework] || selectedHomework}</strong>.
            </div>
            {currentHeaders.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '6px' }}>Assign all to header (optional):</label>
                <select value={bulkCommentHeader} onChange={e => setBulkCommentHeader(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value=''>— Uncategorised —</option>
                  {currentHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            )}
            <textarea autoFocus value={bulkCommentText} onChange={(e) => setBulkCommentText(e.target.value)}
              placeholder={'First comment text\nSecond comment text\nThird comment text'}
              rows={8}
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '12px' }} />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={handleBulkAdd} disabled={!bulkCommentText.trim()}
                style={{ padding: '10px 20px', background: bulkCommentText.trim() ? '#667eea' : '#e2e8f0', color: bulkCommentText.trim() ? 'white' : '#95a5a6', border: 'none', borderRadius: '8px', cursor: bulkCommentText.trim() ? 'pointer' : 'not-allowed', fontWeight: '600' }}>
                Add {bulkCommentText.trim() ? bulkCommentText.split('\n').filter(l => l.trim()).length : 0} Comments
              </button>
              <button onClick={() => { setShowBulkAdd(false); setBulkCommentText(''); setBulkCommentHeader(''); }}
                style={{ padding: '10px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
              {bulkCommentText.trim() && (
                <span style={{ fontSize: '13px', color: '#7f8c8d' }}>
                  {bulkCommentText.split('\n').filter(l => l.trim()).length} lines detected
                </span>
              )}
            </div>
          </div>
        )}

        {/* Single Add Form */}
        {showAddForm && (
          <form onSubmit={handleAddComment} style={{ marginBottom: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '2px solid #c33329' }}>
            {currentHeaders.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '6px' }}>Header (optional):</label>
                <select value={newCommentHeader} onChange={e => setNewCommentHeader(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value=''>— Uncategorised —</option>
                  {currentHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            )}
            <textarea value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Enter new comment..." rows={3}
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '12px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" style={{ padding: '10px 20px', background: '#c33329', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Add to Bank
              </button>
              <button type="button" onClick={() => { setShowAddForm(false); setNewCommentText(''); setNewCommentHeader(''); }}
                style={{ padding: '10px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Comments — grouped by header, then uncategorised */}
        {currentComments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <div>No comments in this section yet</div>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>Add your first comment to help markers provide consistent feedback</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>

            {/* Header groups */}
            {currentHeaders.map(header => (
              <div key={header}
                onDragOver={(e) => { e.preventDefault(); handleDragEnterHeader(header); }}
                onDrop={() => handleDrop(header)}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#667eea', borderRadius: '8px 8px 0 0', marginBottom: '2px' }}>
                  <span style={{ fontWeight: '700', color: 'white', fontSize: '14px', flex: 1 }}>{header}</span>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>{(grouped[header] || []).length} comment{(grouped[header] || []).length !== 1 ? 's' : ''}</span>
                </div>
                {/* Comments under this header */}
                <div style={{ display: 'grid', gap: '2px', border: '2px solid #667eea', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '8px', background: '#f8f9ff' }}>
                  {(grouped[header] || []).length === 0 ? (
                    <div style={headerDropStyle(dragOver.current === header)}>
                      Drop comments here
                    </div>
                  ) : (
                    (grouped[header] || []).map(comment => (
                      <CommentCard key={comment.id} comment={comment}
                        isEditing={editingComment === comment.id}
                        editText={editText} editHeader={editHeader}
                        headers={currentHeaders}
                        onEditText={setEditText} onEditHeader={setEditHeader}
                        onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit} onDelete={handleDeleteComment}
                        onDragStart={handleDragStart}
                        markers={markers} onAllocate={handleAllocateComment}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}

            {/* Uncategorised */}
            {(currentHeaders.length === 0 || grouped['__none__'].length > 0) && (
              <div onDragOver={(e) => { e.preventDefault(); handleDragEnterHeader('__none__'); }}
                onDrop={() => handleDrop(null)}>
                {currentHeaders.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#95a5a6', borderRadius: '8px 8px 0 0', marginBottom: '2px' }}>
                    <span style={{ fontWeight: '700', color: 'white', fontSize: '14px', flex: 1 }}>Uncategorised</span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>{grouped['__none__'].length}</span>
                  </div>
                )}
                <div style={{ display: 'grid', gap: '8px', ...(currentHeaders.length > 0 ? { border: '2px solid #95a5a6', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '8px', background: '#f8f9fa' } : {}) }}>
                  {grouped['__none__'].map(comment => (
                    <CommentCard key={comment.id} comment={comment}
                      isEditing={editingComment === comment.id}
                      editText={editText} editHeader={editHeader}
                      headers={currentHeaders}
                      onEditText={setEditText} onEditHeader={setEditHeader}
                      onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit} onDelete={handleDeleteComment}
                      onDragStart={handleDragStart}
                    />
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>}
    </div>
  );
}

// â”€â”€ CommentCard sub-component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CommentCard({ comment, isEditing, editText, editHeader, headers, onEditText, onEditHeader, onStartEdit, onSaveEdit, onCancelEdit, onDelete, onDragStart, markers, onAllocate }) {
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocateMarkerId, setAllocateMarkerId] = useState('');

  return (
    <div draggable={!isEditing} onDragStart={() => onDragStart(comment.id)}
      style={{ padding: '14px', border: '2px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: isEditing ? 'default' : 'grab' }}>
      {isEditing ? (
        <div>
          {headers.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '4px' }}>Header:</label>
              <select value={editHeader} onChange={e => onEditHeader(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                <option value=''>— Uncategorised —</option>
                {headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          )}
          <textarea value={editText} onChange={(e) => onEditText(e.target.value)} rows={3}
            style={{ width: '100%', padding: '10px', border: '2px solid #667eea', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '10px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onSaveEdit(comment)}
              style={{ padding: '7px 14px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
              Save
            </button>
            <button onClick={onCancelEdit}
              style={{ padding: '7px 14px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '10px', fontSize: '14px', lineHeight: '1.6' }}>{comment.text}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
              {comment.createdByName} • {new Date(comment.createdAt).toLocaleDateString()}
              {comment.header && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#e8eeff', color: '#667eea', borderRadius: '10px', fontWeight: '600' }}>{comment.header}</span>}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => onStartEdit(comment)}
                style={{ padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
                Edit
              </button>
              <button onClick={() => onDelete(comment)}
                style={{ padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
                Delete
              </button>
              <button onClick={() => setShowAllocate(v => !v)}
                style={{ padding: '5px 10px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                title="Copy this comment into a specific marker's own personal comment bank">
                Allocate
              </button>
            </div>
          </div>
          {showAllocate && (
            <div style={{ marginTop: '10px', display: 'flex', gap: '6px', alignItems: 'center' }}>
              <select value={allocateMarkerId} onChange={e => setAllocateMarkerId(e.target.value)}
                style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                <option value=''>Select a marker...</option>
                {(markers || []).map(m => (
                  <option key={m.id} value={m.id}>{m.name}{m.role === 'head_marker' ? ' (Head Marker)' : ''}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!allocateMarkerId) { alert('Select a marker first'); return; }
                  onAllocate(comment, allocateMarkerId);
                  setShowAllocate(false);
                  setAllocateMarkerId('');
                }}
                style={{ padding: '6px 14px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommentBankManager;